import type { CompanyRegistryCheckResult } from '@ballerine/digital-footprint-schemas';
import {
  Building2,
  Calendar,
  Check,
  Copy,
  FileText,
  Globe,
  Hash,
  MapPin,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import { ctw } from '@/common';

dayjs.extend(advancedFormat);

const VENDOR_DISPLAY_NAMES: Record<string, string> = {
  'open-corporates': 'OpenCorporates',
} as const;

type RegistryDataProps = {
  registryData?: CompanyRegistryCheckResult | null;
  className?: string;
};

const CopyButton = React.memo<{ textToCopy: string; className?: string }>(
  ({ textToCopy, className }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard?.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy text:', error);
      }
    };

    return (
      <button
        onClick={handleCopy}
        className={ctw('text-gray-400 hover:text-gray-600 transition-colors', className)}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
        aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      </button>
    );
  },
);

CopyButton.displayName = 'CopyButton';

const DataRow: React.FC<{
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  value: React.ReactNode;
  copyValue?: string;
}> = ({ label, Icon, value, copyValue }) => {
  if (!value) return null;

  return (
    <div className="mb-4 grid grid-cols-2 items-center gap-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-zinc-500 flex-shrink-0" />
        <p className="text-sm text-slate-400">{label}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="text-sm text-gray-900">{value}</div>
        {copyValue && <CopyButton textToCopy={copyValue} />}
      </div>
    </div>
  );
};

const ArrayDataRow: React.FC<{
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  items: Array<{ label: string; value: string }>;
}> = ({ label, Icon, items }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="mb-4 grid grid-cols-2 items-center gap-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-zinc-500 flex-shrink-0" />
        <p className="text-sm text-slate-400">{label}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {items.map((item, index) => (
          <div key={index} className="text-sm text-gray-900 flex items-center gap-1.5">
            <span>{item.label}</span>
            <CopyButton textToCopy={item.value} />
            {index < items.length - 1 && <span className="text-slate-400">,</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const RegistryData: React.FC<RegistryDataProps> = ({ registryData, className }) => {
  if (!registryData || !registryData.success || !registryData.data) {
    const errorMessage = registryData?.error || 'Registry data unavailable';

    return (
      <div className={ctw('rounded-md bg-slate-50 border border-slate-200 p-4', className)}>
        <p className="text-sm text-slate-600">{errorMessage}</p>
      </div>
    );
  }

  const { data } = registryData;

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM Do YYYY');
  };

  const vendorDisplayName = VENDOR_DISPLAY_NAMES[registryData.source] || registryData.source;

  return (
    <div className={className}>
      <div className="text-sm text-slate-500 mb-6 flex items-center gap-2">
        <span>
          Source:{' '}
          {data.openCorporatesUrl ? (
            <a
              href={data.openCorporatesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {vendorDisplayName}
            </a>
          ) : (
            vendorDisplayName
          )}
        </span>
        <span>|</span>
        <span>Fetched: {formatDate(registryData.retrievedAt)}</span>
        <span>|</span>
        <span>Last updated: {formatDate(data.source.retrievedAt)}</span>
      </div>
      {/* Basic Company Information */}
      <DataRow label="Company Name" Icon={Building2} value={data.name} copyValue={data.name} />

      <DataRow
        label="Company Number"
        Icon={Hash}
        value={data.companyNumber}
        copyValue={data.companyNumber}
      />

      {/* Identifiers */}
      {data.identifiers && data.identifiers.length > 0 && (
        <ArrayDataRow
          label="External Identifiers"
          Icon={Hash}
          items={data.identifiers.map(identifier => ({
            label: `${identifier.identifierSystemName}: ${identifier.identifierSystemCode}`,
            value: identifier.identifierSystemCode,
          }))}
        />
      )}

      <DataRow
        label="Jurisdiction"
        Icon={Globe}
        value={data.jurisdictionCode}
        copyValue={data.jurisdictionCode}
      />

      <DataRow
        label="Company Type"
        Icon={FileText}
        value={data.companyType}
        copyValue={data.companyType || undefined}
      />

      <DataRow
        label="Current Status"
        Icon={FileText}
        value={data.currentStatus || (data.isInactive ? 'Inactive' : 'Active')}
        copyValue={data.currentStatus || (data.isInactive ? 'Inactive' : 'Active')}
      />

      {/* Branch Information */}
      {data.isBranch && (
        <DataRow
          label="Branch Status"
          Icon={FileText}
          value={data.branchStatus}
          copyValue={data.branchStatus || undefined}
        />
      )}

      {/* Dates */}
      <DataRow
        label="Incorporation Date"
        Icon={Calendar}
        value={data.incorporationDate}
        copyValue={data.incorporationDate || undefined}
      />

      <DataRow
        label="Dissolution Date"
        Icon={Calendar}
        value={data.dissolutionDate}
        copyValue={data.dissolutionDate || undefined}
      />

      {/* Address */}
      <DataRow
        label="Registered Address"
        Icon={MapPin}
        value={
          data.registeredAddress
            ? data.registeredAddressInFull ||
              [
                data.registeredAddress.streetAddress,
                data.registeredAddress.locality,
                data.registeredAddress.region,
                data.registeredAddress.postalCode,
                data.registeredAddress.country,
              ]
                .filter(Boolean)
                .join(', ')
            : null
        }
        copyValue={
          data.registeredAddress
            ? data.registeredAddressInFull ||
              [
                data.registeredAddress.streetAddress,
                data.registeredAddress.locality,
                data.registeredAddress.region,
                data.registeredAddress.postalCode,
                data.registeredAddress.country,
              ]
                .filter(Boolean)
                .join(', ')
            : undefined
        }
      />

      {/* Agent */}
      <DataRow
        label="Agent Name"
        Icon={Users}
        value={data.agentName}
        copyValue={data.agentName || undefined}
      />

      {/* Number of Employees */}
      <DataRow
        label="Number of Employees"
        Icon={Users}
        value={data.numberOfEmployees?.toString()}
        copyValue={data.numberOfEmployees?.toString()}
      />

      {/* Industries */}
      {data.industries && data.industries.length > 0 && (
        <ArrayDataRow
          label="Industry Codes"
          Icon={Building2}
          items={data.industries.map(industry => ({
            label: `${industry.code}${industry.description ? ` - ${industry.description}` : ''} (${industry.codeSchemeName})`,
            value: `${industry.code} - ${industry.description || 'No description'}`,
          }))}
        />
      )}

      {/* Previous Names */}
      {data.previousNames && data.previousNames.length > 0 && (
        <ArrayDataRow
          label="Previous Names"
          Icon={FileText}
          items={data.previousNames.map(name => ({
            label: `${name.companyName}${name.startDate ? ` (${name.startDate} - ${name.endDate || 'present'})` : ''}`,
            value: name.companyName,
          }))}
        />
      )}

      {/* Alternative Names */}
      {data.alternativeNames && data.alternativeNames.length > 0 && (
        <ArrayDataRow
          label="Alternative Names"
          Icon={FileText}
          items={data.alternativeNames.map(name => ({
            label: `${name.companyName}${name.language ? ` (${name.language})` : ''}${name.type ? ` [${name.type}]` : ''}`,
            value: name.companyName,
          }))}
        />
      )}
    </div>
  );
};
